<template>
  <div>
    <div v-if="initializing" class="text-center">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <b-card v-else body-class="p-0">
      <h5 class="bg-light py-3 px-3 mb-0">
        {{ title }}
      </h5>

      <div class="pt-3 px-3">
        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.requestSubmissionDate') }} :
          </span>

          <date-display :value="value.created" :class="{ 'ml-1': $dir.ltr, 'mr-1': $dir.rtl }"/>
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.transportDate') }} :
          </span>

          <date-display :value="value.transportDate" :class="{ 'ml-1': $dir.ltr, 'mr-1': $dir.rtl }"/>
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.transportType') }} :
          </span>

          <template v-if="value.courierId">
            {{ $t('transports.transportByCourier', { courier: value.courier && value.courier.name }) }}
          </template>

          <template v-else>
            {{ $t('transports.personalTransport') }}
          </template>
        </div>
      </div>

      <h6 class="bg-warning-light py-3 px-3 text-center mb-0">
        <template v-if="value.courierId">
          {{ $t('transports.transportCompanyInfo') }}
        </template>

        <template v-else>
          {{ $t('transports.transporterInfo') }}
        </template>
      </h6>

      <template v-if="value.courierId">
        <div v-if="value.courier && value.courier.profile && value.courier.profile.courierData" class="pt-3 px-3">
          <div class="mb-3">
            <span class="text-muted">
              {{ $t('shared.name') }} :
            </span>

            {{ value.courier.profile.courierData.name }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('couriers.countryCode') }} :
            </span>

            {{ value.courier.profile.courierData.code }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.province') }} :
            </span>

            <template v-if="value.courier.province">
              {{ value.courier.province.name }}
            </template>
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.city') }} :
            </span>

            <template v-if="value.courier.city">
              {{ value.courier.city.name }}
            </template>
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.address') }} :
            </span>

            {{ value.courier.profile.courierData.officialAddress }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.zipCode') }} :
            </span>

            {{ value.courier.profile.courierData.officialZipCode }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('couriers.officialPhoneNumber') }} :
            </span>

            <span dir="ltr">
              {{ value.courier.profile.courierData.officialPhoneCode }} - {{ value.courier.profile.courierData.officialPhoneNumber }}
            </span>
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('shared.email') }} :
            </span>

            {{ value.courier.profile.courierData.officialEmail }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('couriers.operatorName') }} :
            </span>

            {{ value.courier.profile.courierData.operatorName }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('couriers.operatorCellNumber') }} :
            </span>

            {{ value.courier.profile.courierData.operatorCellNumber }}
          </div>
        </div>
      </template>

      <template v-else>
        <div class="pt-3 px-3">
          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.transporterFirstName') }} :
            </span>

            {{ value.transporterFirstName }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.transporterLastName') }} :
            </span>

            {{ value.transporterLastName }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.transporterNationalCode') }} :
            </span>

            {{ value.transporterNationalCode }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.transporterPhoneNumber') }} :
            </span>

            <span dir="ltr">
              {{ value.transporterPhoneNumber }}
            </span>
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.transporterCellNumber') }} :
            </span>

            {{ value.transporterCellNumber }}
          </div>
        </div>
      </template>

      <h6 class="bg-warning-light py-3 px-3 text-center mb-0">
        {{ $t('transports.loadingPlaceInfo') }}
      </h6>

      <div v-if="value.inventory" class="pt-3 px-3">
        <div class="mb-3">
          <span class="text-muted">
            {{ $t('inventories.inventoryName') }} :
          </span>

          {{ value.inventory.name }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('inventories.operatorName') }} :
          </span>

          {{ value.inventory.operatorName }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('inventories.workingHours') }} :
          </span>

          {{ value.inventory.workingHours }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('inventories.phoneNumber') }} :
          </span>

          <span dir="ltr">
            {{ value.inventory.phoneCode }} - {{ value.inventory.phoneNumber }}
          </span>
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('inventories.cellNumber') }} :
          </span>

          {{ value.inventory.cellNumber }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.province') }} :
          </span>

          {{ value.inventory.province.name }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.city') }} :
          </span>

          {{ value.inventory.city.name }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.address') }} :
          </span>

          {{ value.inventory.address }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('transports.zipCode') }} :
          </span>

          {{ value.inventory.zipCode }}
        </div>

        <div class="mb-3">
          <span class="text-muted">
            {{ $t('shared.coordinates') }} :
          </span>

          <geopoint-display :value="value.inventory.coordinates"/>
        </div>
      </div>

      <template v-if="value.courierId">
        <h6 class="bg-warning-light py-3 px-3 text-center mb-0">
          {{ $t('transports.unloadPlace') }}
        </h6>

        <div class="pt-3 px-3">
          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.province') }} :
            </span>

            <template v-if="value.unloadProvince">
              {{ value.unloadProvince.name }}
            </template>
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.city') }} :
            </span>

            <template v-if="value.unloadCity">
              {{ value.unloadCity.name }}
            </template>
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.address') }} :
            </span>

            {{ value.unloadAddress }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.zipCode') }} :
            </span>

            {{ value.unloadZipCode }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.unloadTime') }} :
            </span>

            {{ value.unloadTimeFrom.split(':')[0] }} {{ $t('products.to') }} {{ value.unloadTimeTo.split(':')[0] }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.recipientName') }} :
            </span>

            {{ value.recipientName }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.recipientNationalCode') }} :
            </span>

            {{ value.recipientNationalCode }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.recipientPhoneNumber') }} :
            </span>

            {{ value.recipientPhoneNumber }}
          </div>

          <div class="mb-3">
            <span class="text-muted">
              {{ $t('transports.recipientCellNumber') }} :
            </span>

            {{ value.recipientCellNumber }}
          </div>
        </div>
      </template>
    </b-card>
  </div>
</template>

<script>
import { DateDisplay, GeopointDisplay } from '@/components/shared'
import Breadcrumb from '@/mixins/breadcrumb'

export default {
  name: 'TransportDetail',
  mixins: [Breadcrumb],
  components: {
    DateDisplay,
    GeopointDisplay
  },
  props: ['id'],
  data () {
    return {
      initializing: true,
      title: null
    }
  },
  mounted () {
    this.getModel()
    .then(() => {
      this.setBreadcrumb()
    })
  },
  methods: {
    getModel () {
      return this.$api.transports.getById(this.id, { include: ['Courier', 'UnloadProvince', 'UnloadCity', 'Stock'] })
      .then(res => {
        this.value = res

        const totalUnits = res.transports.map(o => o.bundlesCount).reduce((partialSum, a) => partialSum + a, 0);

        this.title = `
          ${this.$t('transports.transportRequestOf')}
          ${(totalUnits * this.value.bundleRate).toLocaleString()}
          ${this.value.model.product.type.unit.name}
          ${this.value.model.product.title}
        `
        this.initializing = false

        return this.value
      })
      .catch(err => {
        this.$showError(this.$getLocaleErrorMessage(err, 'transports'))
        this.$router.push('/transports')
      })
    },
    setBreadcrumb () {
      const items = []
      let path = ''

      path = '/stocks'
      items.push({ path, text: this.$t('nav.transports') })

      path = `/${this.id}`
      items.push({ path, text: this.title })

      this.commitBreadcrumb(items)
    }
  }
}
</script>
